/* ------------------------------------------------------------------ */
/* Shared demographics / DOB helpers.                                  */
/* Single source of truth for the onboarding wizard (Screen 4) and the */
/* post-onboarding BackfillModal — keep them identical by importing     */
/* from here rather than redefining.                                   */
/* ------------------------------------------------------------------ */

// Minimum age to use Haiven (health product + research arm). Mirror of
// the backend MIN_AGE in app/schemas/auth.py — keep both in sync.
export const MIN_AGE = 18;

// US month/day/year ordering. ``value`` is the zero-padded month used to
// assemble the ISO date; values map 1:1 to nothing DB-side (DOB is a date).
export const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Biological-sex options. ``id`` maps 1:1 to the users.sex DB tokens and
// the CHECK constraint ck_users_sex_allowed.
export const SEX_OPTIONS = [
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
  { id: 'prefer_not_to_say', label: 'Prefer not to say' },
];

// Number of days in a given month/year. Falls back to a leap year (2000)
// when the year isn't chosen yet, so Feb shows 29 until proven otherwise.
export const daysIn = (m, y) => (m ? new Date(Number(y) || 2000, Number(m), 0).getDate() : 31);

// Zero-padded day option strings ('01'..'28/29/30/31') for the chosen
// month + year.
export function dayOptions(month, year) {
  return Array.from({ length: daysIn(month, year) }, (_, i) => String(i + 1).padStart(2, '0'));
}

// Year option strings, most-recent-first, spanning the last `span` years.
export function yearOptions(span = 101) {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: span }, (_, i) => String(currentYear - i));
}

// Assemble M/D/Y selectors into the ISO string the backend expects
// ('YYYY-MM-DD'). Returns null when incomplete OR when the parts don't
// form a real calendar date.
export function assembleDob(month, day, year) {
  if (!(year && month && day)) return null;
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  const probe = new Date(y, m - 1, d);
  const valid =
    probe.getFullYear() === y &&
    probe.getMonth() === m - 1 &&
    probe.getDate() === d;
  return valid ? `${year}-${month}-${day}` : null;
}

// Whole-years age from an ISO 'YYYY-MM-DD' DOB, relative to today.
export function ageFromDob(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const today = new Date();
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() + 1 - m;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age -= 1;
  return age;
}

// Human-friendly DOB for confirmation copy, e.g. 'May 14, 1990'.
export function formatDob(month, day, year) {
  const label = MONTHS.find((mo) => mo.value === month)?.label;
  if (!label || !day || !year) return '';
  return `${label} ${Number(day)}, ${year}`;
}
